import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Actions,
} from "../../styles";

import SimpleCheckbox from "../../../../../../../components/SimpleCheckbox";
import { showModal } from "../../../../../../../store/actions/modal";
import { SessionInDto } from "../../../../../../../types/session.dto";

import chainIcon from "../../../../../../../assets/icons/chain-regular.svg";
import duplicateIcon from "../../../../../../../assets/icons/post-copy-link-icon.svg";
import sessionEditIcon from "../../../../../../../assets/icons/post-edit-icon.svg";
import trashIcon from "../../../../../../../assets/icons/trash.svg";
import chevronDownIcon from "../../../../../../../assets/icons/chevron-down.svg";
import {
  MenuWrapper,
  MenuToggle,
  MenuToggleIcon,
  MenuList,
  MenuListItem,
  MenuItemButton,
  MenuItemLink,
  MenuItemIcon,
  MenuSeparator,
  CheckboxWrapper,
} from "./styles";

interface SessionCardActionsProps {
  session: SessionInDto;
  ownGroup: boolean;
  isMine: boolean;
  groupInformation: any;
  setSessionToDelete?: (session: SessionInDto) => void;
  showCheckbox: boolean;
  isSelected: boolean;
  onSelectionChange?: (sessionId: number, checked: boolean) => void;
  className?: string;
}

const SessionCardActions = ({
  session,
  ownGroup,
  isMine,
  groupInformation,
  setSessionToDelete,
  showCheckbox,
  isSelected,
  onSelectionChange,
  className,
}: SessionCardActionsProps) => {
  const dispatch = useDispatch();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const canManageSession = ownGroup || isMine;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!canManageSession) {
      setIsMenuOpen(false);
    }
  }, [canManageSession]);

  if (!canManageSession) {
    return null;
  }

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleCopyLink = async () => {
    const link = `${process.env.REACT_APP_SITE_BASE_URL}/group/${groupInformation?.id}/s/${session.id}`;

    try {
      await navigator.clipboard.writeText(link);
    } catch (error) {
      console.error("No se pudo copiar el enlace de la sesión", error);
    }

    closeMenu();
  };

  const handleDeleteClick = () => {
    if (setSessionToDelete) {
      setSessionToDelete(session);
      dispatch(showModal("modal-delete-session"));
    }
    closeMenu();
  };

  const handleCheckboxChange = (checked: boolean) => {
    onSelectionChange?.(session.id, checked);
  };

  const baseDashboardPath = groupInformation?.group_type === "free_experience"
    ? "community/events"
    : groupInformation?.group_type;

  return (
    <Actions className={className}>
      <MenuWrapper ref={menuRef}>
        <MenuToggle
          type="button"
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          onClick={toggleMenu}
        >
          Acciones <MenuToggleIcon icon={chevronDownIcon} color="currentColor" size="13px" isOpen={isMenuOpen} />
        </MenuToggle>

        {isMenuOpen && (
          <MenuList role="menu">
            <MenuListItem>
              <MenuItemButton type="button" onClick={handleCopyLink} role="menuitem">
                <MenuItemIcon icon={chainIcon} color="currentColor" size="14px" />
                Copiar enlace
              </MenuItemButton>
            </MenuListItem>

            <MenuListItem>
              <MenuItemLink
                to={`/dashboard/${baseDashboardPath}/${groupInformation?.id}/sessions/duplicate/${session.id}`}
                onClick={closeMenu}
                role="menuitem"
              >
                <MenuItemIcon icon={duplicateIcon} color="currentColor" size="14px" />
                Duplicar
              </MenuItemLink>
            </MenuListItem>

            <MenuListItem>
              <MenuItemLink
                to={`/dashboard/${baseDashboardPath}/${groupInformation?.id}/sessions/${session.id}`}
                onClick={closeMenu}
                role="menuitem"
              >
                <MenuItemIcon icon={sessionEditIcon} color="currentColor" size="14px" />
                Editar
              </MenuItemLink>
            </MenuListItem>

            <MenuSeparator />

            <MenuListItem>
              <MenuItemButton type="button" onClick={handleDeleteClick} role="menuitem">
                <MenuItemIcon icon={trashIcon} color="currentColor" size="14px" />
                Eliminar
              </MenuItemButton>
            </MenuListItem>
          </MenuList>
        )}
      </MenuWrapper>

      {showCheckbox && (
        <CheckboxWrapper>
          <SimpleCheckbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            id={`session-checkbox-${session.id}`}
            value={session.id}
          />
        </CheckboxWrapper>
      )}
    </Actions>
  );
};

export default SessionCardActions;

